/**
 * Core scan types.
 *
 * Three tiers exist because not every fingerprinting technique is observable
 * from a static page. Tier is a first-class, user-visible property: a
 * technique that cannot honestly be measured is catalogued, never faked.
 *
 *  - 'client'  measured in the page (JS, WebGL, WebGPU, codecs, fonts...)
 *  - 'edge'    relayed by the tiny Cloudflare Worker (request.cf, headers)
 *  - 'server'  needs raw TLS/H2/QUIC/TCP/STUN visibility; catalogued only
 */

/** How observable a technique is from a static site behind an edge. */
export type Tier = 'client' | 'edge' | 'server';

/** The signal family a probe or finding belongs to (organises the UI). */
export type Layer =
  | 'general'
  | 'browser'
  | 'gpu'
  | 'media'
  | 'locale'
  | 'display'
  | 'network'
  | 'tls'
  | 'webrtc'
  | 'hardware'
  | 'engine'
  | 'behaviour'
  | 'sab';

/** Coarse scoring buckets. Deliberately only four, so the score stays auditable. */
export type Category = 'hardware' | 'browser' | 'network' | 'behaviour';

/**
 * Result state of a single signal.
 *
 * 'denied' is deliberately distinct from 'unavailable': a user who blocks a
 * permission and a browser that lacks the API are very different facts to a
 * fingerprint reader, and collapsing them hides real information.
 */
export type Status =
  | 'ok'
  | 'unavailable'
  | 'denied'
  | 'suspended'
  | 'unsupported'
  | 'error'
  | 'timeout'
  | 'skipped';

export type Severity = 'info' | 'low' | 'medium' | 'high';
export type Confidence = 'low' | 'medium' | 'high';
export type Origin = 'client' | 'edge' | 'derived';

/** One measured value plus how it was obtained. */
export interface SignalValue<T = unknown> {
  value: T | null;
  status: Status;
  origin: Origin;
  durationMs: number;
  error?: string;
}

/**
 * A detection result.
 *
 * `scoreContribution` is intentionally NOT the only field the UI reads: the
 * human-readable `title`/`detail` carry the explanation. We withhold rule
 * thresholds from the public API (see publicApi.ts) so the output stays useful
 * for debugging without reading like a bypass guide.
 */
export interface Finding {
  /** Stable dotted id, e.g. 'coherence.ua-platform-mismatch'. Used as a React key. */
  id: string;
  severity: Severity;
  category: Category;
  layer: Layer;
  confidence: Confidence;
  title: string;
  detail: string;
  /** URLs from src/data, or doc anchors. Every finding must be attributable. */
  sources: string[];
  /** The observed values that actually triggered the rule. */
  evidence: string[];
  scoreContribution: number;
}

/** Stable fingerprint-part identifiers. The first nine match fingerprint-scan.com. */
export type PartId =
  // parity parts
  | 'gpu'
  | 'screen'
  | 'localeTimezone'
  | 'deviceInfo'
  | 'browserFeatures'
  | 'mediaQueries'
  | 'media'
  | 'webAssembly'
  | 'webRtc'
  // additional parts
  | 'webgpu'
  | 'tls'
  | 'ice'
  | 'network'
  | 'fonts'
  | 'fontShaping'
  | 'engineTiming'
  | 'codecs'
  | 'sab';

export const PARITY_PARTS: PartId[] = [
  'gpu',
  'screen',
  'localeTimezone',
  'deviceInfo',
  'browserFeatures',
  'mediaQueries',
  'media',
  'webAssembly',
  'webRtc',
];

export const ALL_PARTS: PartId[] = [
  ...PARITY_PARTS,
  'webgpu',
  'tls',
  'ice',
  'network',
  'fonts',
  'fontShaping',
  'engineTiming',
  'codecs',
  'sab',
];

/** Context handed to a probe's run() function. */
export interface ProbeContext {
  /** Wall-clock deadline for the whole scan. */
  deadline: number;
  /** Results already collected, for cross-probe coherence checks. */
  get(part: PartId): string | undefined;
  /** Read a signal value already produced by any probe. */
  readSignal<T = unknown>(probeId: string, signal: string): T | undefined;
  /** Opt-in user gesture has been granted (unlocks motion/permission probes). */
  gestureGranted: boolean;
}

export interface ProbeResult {
  signals: Record<string, SignalValue>;
  findings?: Finding[];
  /** Normalized inputs contributed to a fingerprint part; hashed centrally. */
  parts?: Partial<Record<PartId, unknown>>;
}

export interface Probe {
  id: string;
  label: string;
  layer: Layer;
  tier: Tier;
  category: Category;
  description: string;
  /** Specs / research this probe is based on. Surfaced in the UI. */
  sources?: string[];
  /** Requires a user gesture, permission prompt, or long budget. */
  requiresGesture?: boolean;
  /**
   * Probe ids that must settle before this one starts. Used by probes that
   * reason ACROSS siblings (e.g. coherence reads the UA, the WebGL renderer and
   * the client hints). The runner executes these in a later wave so the
   * dependency is real rather than a race.
   */
  after?: string[];
  /** Hard per-probe timeout. The runner enforces this regardless of behaviour. */
  budgetMs: number;
  run(ctx: ProbeContext): Promise<ProbeResult>;
}

/** Live status of one probe during a scan. */
export type ProbeState = 'pending' | 'running' | 'done' | 'timeout' | 'error' | 'skipped';

export interface ProbeOutcome {
  probe: Probe;
  state: ProbeState;
  signals: Record<string, SignalValue>;
  findings: Finding[];
  durationMs: number;
  error?: string;
  /** Normalized part inputs contributed by this probe; hashed centrally. */
  partInputs?: Partial<Record<PartId, unknown>>;
}

export type ScanStatus = 'IDLE' | 'COLLECTING' | 'COLLECT_SUCCESSFUL' | 'COLLECT_PARTIAL' | 'COLLECT_FAILED';

/** Coarse per-category explanation. Never includes the rule that fired. */
export interface ScoreReason {
  category: Category;
  level: 'low' | 'medium' | 'high';
  /** Points this category contributed before the global cap. */
  score: number;
}

/** The complete public result shape, also exposed on window.FP_SCAN. */
export interface ScanResult {
  status: ScanStatus;
  fingerprintHash: string;
  fingerprintId: string;
  fingerprintParts: Partial<Record<PartId, string>>;
  botScore: number;
  reasons: ScoreReason[];
  /** Signals grouped by probe, then by signal name. */
  signals: Record<string, Record<string, SignalValue>>;
  findings: Finding[];
  /** Cross-context (main / iframe / worker) divergence report. */
  crossContext: CrossContextReport;
  startedAt: number;
  finishedAt: number;
}

export interface CrossContextReport {
  available: boolean;
  contexts: Array<'main' | 'iframe' | 'worker'>;
  /** signal name -> values per context that disagreed. */
  divergences: Array<{
    signal: string;
    main: unknown;
    iframe?: unknown;
    worker?: unknown;
  }>;
}
